import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  approveScript,
  dispatchVideo,
  fetchDispatchStatus,
  fetchRenderStatus,
  leaveDispatch,
  submitRender,
} from "../api/video";

/** Poll render status while a render is pending; the GET stores the clip on completion. */
export function useVideoRenderStatus(id: string, opts: { live: boolean }) {
  return useQuery({
    queryKey: ["video-render", id],
    queryFn: () => fetchRenderStatus(id),
    enabled: !!id && opts.live,
    refetchInterval: (query) => {
      const data = query.state.data as { status?: string } | undefined;
      // Poll while HeyGen is still working; STOP on any terminal status
      // (completed / failed / error) so a failed render doesn't poll forever.
      const terminal = ["completed", "failed", "error"];
      if (data?.status && !terminal.includes(data.status)) return 5_000;
      return false;
    },
    retry: false,
  });
}

/** Poll the Recall bot status while a dispatch is active (display-only). */
export function useVideoDispatchStatus(id: string, opts: { live: boolean }) {
  return useQuery({
    queryKey: ["video-dispatch", id],
    queryFn: () => fetchDispatchStatus(id),
    enabled: !!id && opts.live,
    refetchInterval: opts.live ? 5_000 : false,
    retry: false,
  });
}

export function useApproveScript(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => approveScript(id),
    onSuccess: (data) => qc.setQueryData(["engagement", id], data),
  });
}

export function useSubmitRender(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => submitRender(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["engagement", id] });
      // Reset the render-status poll so a re-render after a failed/terminal
      // status starts polling the NEW video instead of staying on the old one.
      void qc.invalidateQueries({ queryKey: ["video-render", id] });
    },
  });
}

export function useDispatchVideo(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (meetingUrl: string) => dispatchVideo(id, meetingUrl),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["engagement", id] }),
  });
}

export function useLeaveDispatch(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => leaveDispatch(id),
    onSuccess: (data) => qc.setQueryData(["engagement", id], data),
  });
}
